import { Box, Button, Divider, Typography, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormatBaseUnitQuantity } from '@kheops/utils';
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { generatePath, useNavigate } from 'react-router';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { contractAtom, currentContextAtom } from '../state';
import i18n from '../i18n';
import Sheet from '../common/components/Sheet';
import SizedImage from '../common/components/SizedImage';
import FileUtils from '../common/utils/file.utils';
import QualityLabels from '../search/product/QualityLabels';
import PackagingPagePrices from '../products/packagingPage/PackagingPagePrices';
import SocialTagCard from '../social-proof/SocialTagCard';
import { PackagingFamilyTypeIntl } from '../common/i18n/product-family.translation';
import SocialTags from '../social-proof/SocialTags';
import UnsignedSupplierInfoCard from '../company/companyInfo/UnsignedSupplierInfoCard';
import DiscoveryDialogPackagings from '../discovery/as-buyer/DiscoveryDialogPackagings';
import { RoutePaths } from '../routes/AppRoutes';
import { usePackagingSheetDataQuery } from '../queries/__generated__/packagingSheetData.generated';
import WishlistProductButton from '../wishlist/WishlistProductButton';
import ResumeNegotiationButton from '../company/ResumeNegotiationButton';
import ContractRequestButton from '../company/ContractRequestButton';
import useRelevantSocialTags from '../social-proof/hooks/useRelevantSocialTags';

interface PackagingSheetDrawerProps {
  companyId: string;
  packagingSku: number;
  isOpen: boolean;
  close: () => void;
}

export default function PackagingSheetDrawer({ companyId, packagingSku, isOpen, close }: PackagingSheetDrawerProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'products', 'contracts', 'product-family']);
  const navigate = useNavigate();
  const { companyId: contextCompanyId } = useAtomValue(currentContextAtom);
  const numberFormat = useMemo(() => new Intl.NumberFormat(i18n.resolvedLanguage, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }), []);

  const companyContractAtom = useMemo(() => {
    return contractAtom(contextCompanyId, companyId);
  }, [contextCompanyId, companyId]);
  const contract = useAtomValue(companyContractAtom);

  const { data: packagingSheetData, loading: isPackagingSheetLoading } = usePackagingSheetDataQuery({
    variables: { sku: packagingSku, companyId: companyId },
    skip: !isOpen,
  });

  const sheetData = useMemo(() => {
    if (packagingSheetData?.packaging?.length && packagingSheetData.company_by_pk && packagingSheetData.company_product_count_by_sub_family) {
      return {
        packagingData: packagingSheetData.packaging[0],
        company: packagingSheetData.company_by_pk,
        productSubFamilies: packagingSheetData.company_product_count_by_sub_family
      }
    }
    return undefined;
  }, [packagingSheetData]);

  const displayedPackagingSocialTags = useRelevantSocialTags(sheetData?.packagingData.base_unit.social_tags ?? [], { rules: { 1: 1, 2: 1 }, max: 2 });

  if (isPackagingSheetLoading || !sheetData) {
    return (
      <Sheet open={isOpen} width={400} onClose={close}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <Skeleton variant="rounded" width="100%" height={600} sx={{ borderRadius: 2 }} />
        </Box>
      </Sheet>
    );
  }
  const { company, productSubFamilies, packagingData } = sheetData;
  const { product, base_unit: baseUnit, ...packaging } = packagingData;

  return (
    <Sheet
      open={isOpen}
      width={400}
      onClose={close}
      title={product.name}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: 'column',
          '.Kheops-ServiceCard-card': {
            backgroundColor: 'surfaceContainer.main'
          }
        }}
      >
        <Box sx={{ position: 'relative', width: '100%' }}>
          <SizedImage
            src={FileUtils.BuildPackagingImageUrl({
              photo: baseUnit.main_photo,
              type: product.sub_family,              
            })}
            borderRadius={3}
            alt={t('products:csu')}
            width="100%"
            sx={{
              aspectRatio: '4 / 3',
                '& img' : {
                  height: '100%',
                  width: '100%',
                  objectFit: 'contain'
                }
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <QualityLabels labels={product.labels.map((l) => l.label)} size={40} nbToDisplay={4} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
            }}
          >
            <WishlistProductButton
              supplierId={companyId}
              productSku={product.sku}
            />
          </Box>
        </Box>
        <Typography variant="bodyMedium">
          {FormatBaseUnitQuantity(baseUnit, 'fr', numberFormat)}
        </Typography>
        <PackagingPagePrices baseUnit={baseUnit} packaging={packaging} vatRate={product.vat_rate!} />
        {!!baseUnit.social_tags?.length && (
          <Box>
            {displayedPackagingSocialTags.map((tag) => (
              <SocialTagCard key={tag.type} tag={tag} entity="BASE_UNIT" />
            ))}
          </Box>
        )}
        <Divider flexItem />
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <SizedImage
                src={FileUtils.BuildCompanyImageUrl({
                  photo: company.photos?.[0]?.photo,
                  type: company.business_profile,
                  size: { fit: 'cover', width: 168, height: 168 },
                })}
                alt={t('common:company')}
                width={48}
                borderRadius="48px"
              />
              <Box>
                <Typography onClick={() => navigate(generatePath(RoutePaths.CONTRACT_COMPANY, { companyId }))} variant="titleMedium" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                  {company.tradeName}
                </Typography>

                {productSubFamilies.length > 0 && (
                  <Typography
                    variant="bodySmall"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t(`product-family:${PackagingFamilyTypeIntl[productSubFamilies[0].sub_family as Product_Sub_Family_Name_Enum]}`)}
                    {productSubFamilies.length > 1 && ` (+${productSubFamilies.length - 1})`}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <SocialTags tags={company.social_tags} displayRules={{ rules: { 1: 1, 2: 1 }, max: 2 }} />
            </Box>
          </Box>
          <UnsignedSupplierInfoCard
            company={company}
            contract={contract}
            compact
            displayCompanyDescription={false}
            displayCompanyAddress={false}
            displaySocialTagCards={false}
            companyServicesSx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              p: 1,
              '.Kheops-CompanyServicesInfoLight-toggle': {
                width: '100%',
              }
            }}
          />
          <DiscoveryDialogPackagings
            sx={{ 
              display: 'flex',              
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 1.5,
              '& > div': {
                backgroundColor: 'surfaceContainerHigh.main',
                boxShadow: 'none'
              },
            }}
            companyId={company.id}
          />
        </Box>        
      </Box>
      <Box
          sx={{
            bgcolor: 'surfaceContainerLow.main',
            mt: 'auto',
            position: 'sticky',
            bottom: -16,
            mx: -2,
            mb: -2,
            p: 2,            
          }}
        >
          <Divider flexItem />
          <Box sx={{ display: 'flex', mt: 2, gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(`${generatePath(RoutePaths.PACKAGING, { productSku: product.sku.toString(), companyId })}?csu=${baseUnit.sku}&packaging=${packaging.sku}`)}>
              {t('products:view_product_form')}
            </Button>
            {contract && (
              <ResumeNegotiationButton contract={contract} />
            )}
            {!contract && (
              <ContractRequestButton
                companyId={company.id}
                buttonContent={t('common:contact_supplier')}
                buttonProps={{
                  sx: {
                    width: 'fit-content',
                  },
                }}
              />
            )}
          </Box>
        </Box>
    </Sheet>
  );
}
